import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import {
  AccountType,
  CreateEntitlementRequest,
  EntitlementDto,
  EntitlementValueType,
  UpdateEntitlementRequest,
  entitlementService,
} from '../api/adminServices'
import { useToast } from '../components/useToast'
import { EmptyState, ErrorCard, TableSkeleton } from '../components/Feedback'
import { ResizableTableCell, stickyActionsHeaderSx, stickyActionsBodySx } from '../components/ResizableTable'
import PageRefreshButton from '../components/PageRefreshButton'

interface FormState {
  entitlementKey: string
  name: string
  descriptionKey: string
  accountType: AccountType
  valueType: EntitlementValueType
  defaultEnabled: boolean
  isActive: boolean
  sortOrder: string
}

const emptyForm: FormState = {
  entitlementKey: '',
  name: '',
  descriptionKey: '',
  accountType: 'SHIPPER',
  valueType: 'BOOLEAN',
  defaultEnabled: false,
  isActive: true,
  sortOrder: '0',
}

function num(s: string): number | undefined {
  if (s === '' || s == null) return undefined
  const n = Number(s)
  return Number.isFinite(n) ? n : undefined
}

/**
 * Entitlement (plan add-on) catalog. Define the features that subscription plans can grant;
 * enable/disable each feature per plan from the Subscription Plans page ("Manage add-ons").
 * SHIPPER and FREIGHTER keep separate catalogs.
 */
export default function EntitlementsPage() {
  const [accountTypeFilter, setAccountTypeFilter] = useState<'ALL' | AccountType>('ALL')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<EntitlementDto | null>(null)
  const [formData, setFormData] = useState<FormState>(emptyForm)
  const [formError, setFormError] = useState('')

  const toast = useToast()
  const queryClient = useQueryClient()

  const { data, isLoading, isError, isFetching, error, refetch } = useQuery({
    queryKey: ['entitlements'],
    queryFn: () => entitlementService.listCatalog(),
  })

  const createMutation = useMutation({
    mutationFn: (body: CreateEntitlementRequest) => entitlementService.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entitlements'] })
      setDialogOpen(false)
      setFormError('')
      toast.success('Entitlement created')
    },
    onError: (e: any) => setFormError(e?.response?.data?.message || e?.message || 'Failed to create'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateEntitlementRequest }) =>
      entitlementService.update(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entitlements'] })
      setDialogOpen(false)
      setFormError('')
      toast.success('Entitlement updated')
    },
    onError: (e: any) => setFormError(e?.response?.data?.message || e?.message || 'Failed to update'),
  })

  const all: EntitlementDto[] = useMemo(() => data ?? [], [data])
  const visible = useMemo(
    () => (accountTypeFilter === 'ALL' ? all : all.filter((e) => e.accountType === accountTypeFilter)),
    [all, accountTypeFilter]
  )

  const handleOpen = (e: EntitlementDto | null) => {
    setEditing(e)
    setFormError('')
    if (e) {
      setFormData({
        entitlementKey: e.entitlementKey,
        name: e.name,
        descriptionKey: e.descriptionKey || '',
        accountType: e.accountType,
        valueType: e.valueType,
        defaultEnabled: !!e.defaultEnabled,
        isActive: e.isActive !== false,
        sortOrder: e.sortOrder != null ? String(e.sortOrder) : '0',
      })
    } else {
      setFormData(emptyForm)
    }
    setDialogOpen(true)
  }

  const handleSave = () => {
    if (!formData.name.trim()) {
      setFormError('Name is required')
      return
    }
    if (!editing) {
      if (!formData.entitlementKey.trim()) {
        setFormError('Key is required when creating')
        return
      }
      if (!/^[A-Z0-9_]+$/.test(formData.entitlementKey.trim())) {
        setFormError('Key must be UPPER_SNAKE_CASE (A-Z, 0-9, underscore)')
        return
      }
    }

    if (editing) {
      const body: UpdateEntitlementRequest = {
        name: formData.name.trim(),
        descriptionKey: formData.descriptionKey.trim() || undefined,
        valueType: formData.valueType,
        defaultEnabled: formData.defaultEnabled,
        isActive: formData.isActive,
        sortOrder: num(formData.sortOrder),
      }
      updateMutation.mutate({ id: editing.id, body })
    } else {
      const body: CreateEntitlementRequest = {
        entitlementKey: formData.entitlementKey.trim(),
        name: formData.name.trim(),
        descriptionKey: formData.descriptionKey.trim() || undefined,
        accountType: formData.accountType,
        valueType: formData.valueType,
        defaultEnabled: formData.defaultEnabled,
        isActive: formData.isActive,
        sortOrder: num(formData.sortOrder) ?? 0,
      }
      createMutation.mutate(body)
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2} mb={3}>
        <TextField
          select
          size="small"
          label="Account type"
          value={accountTypeFilter}
          onChange={(e) => setAccountTypeFilter(e.target.value as any)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="ALL">All</MenuItem>
          <MenuItem value="SHIPPER">Shipper</MenuItem>
          <MenuItem value="FREIGHTER">Freighter</MenuItem>
        </TextField>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen(null)}>
          Create Add-on
        </Button>
        <PageRefreshButton onRefresh={() => refetch()} loading={isFetching} />
      </Stack>

      {isLoading ? (
        <Paper sx={{ flex: 1, minHeight: 0 }}>
          <TableSkeleton rows={6} columns={7} />
        </Paper>
      ) : isError ? (
        <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ErrorCard title="Failed to load add-ons" message={(error as Error)?.message} onRetry={() => refetch()} />
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Table sx={{ height: visible.length === 0 ? '100%' : undefined }}>
            <TableHead>
              <TableRow>
                <ResizableTableCell>Name</ResizableTableCell>
                <ResizableTableCell>Key</ResizableTableCell>
                <ResizableTableCell>Account Type</ResizableTableCell>
                <ResizableTableCell>Type</ResizableTableCell>
                <ResizableTableCell>Default</ResizableTableCell>
                <ResizableTableCell>Status</ResizableTableCell>
                <ResizableTableCell sx={stickyActionsHeaderSx}>Actions</ResizableTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ borderBottom: 'none', verticalAlign: 'middle' }}>
                    <EmptyState
                      title="No add-ons defined"
                      description="Create an entitlement to gate a feature (e.g. IMPORT_EXCEL) on your plans."
                    />
                  </TableCell>
                </TableRow>
              )}
              {visible.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell>{e.name}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{e.entitlementKey}</TableCell>
                  <TableCell>
                    <Chip
                      label={e.accountType}
                      size="small"
                      color={e.accountType === 'SHIPPER' ? 'info' : 'secondary'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{e.valueType}</TableCell>
                  <TableCell>
                    <Chip
                      label={e.defaultEnabled ? 'On' : 'Off'}
                      size="small"
                      color={e.defaultEnabled ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={e.isActive !== false ? 'Active' : 'Inactive'}
                      size="small"
                      color={e.isActive !== false ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell sx={stickyActionsBodySx}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpen(e)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit Add-on — ${editing.name}` : 'Create Add-on'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Key *"
                  value={formData.entitlementKey}
                  onChange={(e) =>
                    setFormData({ ...formData, entitlementKey: e.target.value.toUpperCase() })
                  }
                  fullWidth
                  size="small"
                  disabled={!!editing}
                  helperText={editing ? 'Immutable after creation' : 'UPPER_SNAKE_CASE, e.g. IMPORT_EXCEL'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description i18n key"
                  value={formData.descriptionKey}
                  onChange={(e) => setFormData({ ...formData, descriptionKey: e.target.value })}
                  fullWidth
                  size="small"
                  helperText="e.g. subscription.entitlement.import_excel.description"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Account Type *"
                  value={formData.accountType}
                  onChange={(e) => setFormData({ ...formData, accountType: e.target.value as AccountType })}
                  fullWidth
                  size="small"
                  disabled={!!editing}
                  helperText={editing ? 'Immutable after creation' : ''}
                >
                  <MenuItem value="SHIPPER">Shipper</MenuItem>
                  <MenuItem value="FREIGHTER">Freighter</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Value Type *"
                  value={formData.valueType}
                  onChange={(e) => setFormData({ ...formData, valueType: e.target.value as EntitlementValueType })}
                  fullWidth
                  size="small"
                  helperText="BOOLEAN = on/off; LIMIT = numeric quota"
                >
                  <MenuItem value="BOOLEAN">Boolean (on/off)</MenuItem>
                  <MenuItem value="LIMIT">Limit (quota)</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Sort Order"
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: e.target.value })}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.defaultEnabled}
                      onChange={(e) => setFormData({ ...formData, defaultEnabled: e.target.checked })}
                    />
                  }
                  label="Default enabled"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Fallback when a plan has no explicit setting. Keep off for fail-closed features.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
